import { DataProvider } from 'react-admin';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import {
    MarketMetadata,
    PriceTimeseries,
    EventMetadata,
    CandleDataPoint,
    MarketAnalysisData,
    PolymarketCandleMetadata,
    PolymarketEvent,
    PolymarketMarket,
} from './types';
import { waitForAuth } from '../../../../data/authUtils';

// Firebase data provider for Polymarket odds data
// Metadata fetches use direct Firestore access for performance
// Candle data fetches still use the react-admin dataProvider

interface GetListParams {
    pagination: { page: number; perPage: number };
    sort: { field: string; order: 'ASC' | 'DESC' };
    filter?: Record<string, any>;
}

interface GetListResult<T> {
    data: T[];
    total: number;
}

// Raw Firebase document structure from _metadata collection
// Now includes market_info directly (no longer need to fetch from candles)
interface RawMetadataDoc {
    id: string;
    name?: string;        // Short identifier (e.g., "polymarket:xxx") - used as token ID
    period?: number;
    series?: string;      // Full composite series ID (e.g., "bogg.polymarket:xxx.down.polymarket_candle.60000000000")
    entity?: string;      // Outcome like "down" or "up", "yes" or "no"
    type?: string;
    source?: string;
    first_timestamp?: number;  // First candle timestamp in nanoseconds
    last_timestamp?: number;   // Last candle timestamp in nanoseconds
    market_info?: {
        condition_id?: string;
        end_date?: any;
        event_start_time?: any;
        event_title?: string;
        market_duration?: [number, number];
        outcome?: string;
        question?: string;
        start_date?: any;
    };
}

// Raw Firebase candle document structure
// Note: market_info is no longer in candle data - it's now in _metadata
interface RawCandleDoc {
    id: string;
    timestamp: any;
    data?: {
        asset?: string;
        base_volume?: number;
        close?: number;
        close_timestamp?: any;
        high?: number;
        low?: number;
        open?: number;
        open_timestamp?: any;
        quote_volume?: number;
        trade_count?: number;
        vwap?: number;
    };
    entity?: string;
    name?: string;
    period?: { secs: number; nanos: number };
    series?: string;
    source?: string;
    type?: string;
}

// Cache configuration
const CACHE_CONFIG = {
    ttlMs: 30 * 60 * 1000,   // 30 minutes (metadata changes infrequently)
    maxDocuments: 2000,       // Matches the Firestore query limit
};

// localStorage cache for events list (stale-while-revalidate)
const EVENTS_CACHE_KEY = 'polymarket_events_cache_v2';
const EVENTS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Cache for metadata (now includes market_info directly)
let metadataCache: {
    data: RawMetadataDoc[] | null;
    fetchedAt: number;
    documentCount: number;
} = { data: null, fetchedAt: 0, documentCount: 0 };

const CACHE_TTL = CACHE_CONFIG.ttlMs;

// Timer for automatic cache expiration
let cacheExpirationTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Schedule automatic cache expiration after TTL
 * Ensures memory is reclaimed even if no new requests come in
 */
function scheduleCacheExpiration(): void {
    if (cacheExpirationTimer) {
        clearTimeout(cacheExpirationTimer);
    }
    cacheExpirationTimer = setTimeout(() => {
        clearMetadataCache();
        cacheExpirationTimer = null;
    }, CACHE_TTL);
}

/**
 * Convert various timestamp formats to Date
 */
function toDate(ts: any): Date {
    if (!ts) {
        console.warn('[firebaseDataProvider] toDate received null/undefined, using current date');
        return new Date();
    }
    if (ts instanceof Date) return ts;
    if (typeof ts === 'string') {
        const parsed = new Date(ts);
        if (isNaN(parsed.getTime())) {
            console.warn('[firebaseDataProvider] Invalid date string:', ts);
            return new Date();
        }
        return parsed;
    }
    if (ts.toDate && typeof ts.toDate === 'function') return ts.toDate();
    if (ts.seconds) return new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000);
    console.warn('[firebaseDataProvider] Unknown timestamp format:', ts);
    return new Date();
}

/**
 * Fetch all metadata documents directly from Firestore.
 * Bypasses react-admin-firebase to avoid per-document serialization overhead
 * and the 10K document cap on getList(). Read-only access - no CRUD needed.
 */
async function fetchAllMetadata(): Promise<RawMetadataDoc[]> {
    const now = Date.now();
    if (metadataCache.data && now - metadataCache.fetchedAt < CACHE_TTL) {
        return metadataCache.data;
    }

    try {
        await waitForAuth();

        const snapshot = await firebase.firestore()
            .collection('timeseries')
            .doc('polymarket_candle')
            .collection('_metadata')
            .orderBy('last_timestamp', 'desc')
            .limit(2000)
            .get();

        const rawMetadata: RawMetadataDoc[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        } as RawMetadataDoc));

        // Filter to only docs with a valid name (token ID)
        const validData = rawMetadata.filter(d => d.name);

        if (validData.length === 0) {
            console.warn('[firebaseDataProvider] Metadata fetch returned 0 documents - not caching');
            return validData;
        }

        // Warn if query hit the limit (some documents may be missing)
        if (validData.length >= CACHE_CONFIG.maxDocuments) {
            console.warn(`[firebaseDataProvider] Metadata result hit the ${CACHE_CONFIG.maxDocuments}-doc Firestore limit - some documents may be missing`);
        }

        metadataCache.data = validData;
        metadataCache.fetchedAt = now;
        metadataCache.documentCount = validData.length;

        // Schedule automatic cache expiration
        scheduleCacheExpiration();

        console.log(`[Odds] Loaded ${validData.length} metadata documents via direct Firestore`);
        return metadataCache.data;
    } catch (error) {
        console.error('Error fetching polymarket metadata:', error);
        throw error;
    }
}

/**
 * Helper to extract fields from metadata doc's market_info
 */
function getMetadataFields(m: RawMetadataDoc): {
    eventTitle: string;
    question: string;
    conditionId: string;
    endDate: any;
    outcome: string;
} {
    const mi = m.market_info || {};
    return {
        eventTitle: mi.event_title || '',
        question: mi.question || '',
        conditionId: mi.condition_id || '',
        endDate: mi.end_date,
        outcome: mi.outcome || '',
    };
}

/**
 * Extract unique events from metadata, grouped by event_title
 */
function extractEventsFromMetadata(metadata: RawMetadataDoc[]): EventMetadata[] {
    const eventsMap = new Map<string, EventMetadata>();

    for (const m of metadata) {
        const { eventTitle, endDate: rawEndDate } = getMetadataFields(m);
        if (!eventTitle) continue;

        if (!eventsMap.has(eventTitle)) {
            let endDate = '';
            try {
                endDate = rawEndDate ? toDate(rawEndDate).toISOString() : '';
            } catch {
                endDate = '';
            }

            eventsMap.set(eventTitle, {
                id: eventTitle, // Use event_title as unique ID
                title: eventTitle,
                category: 'Crypto', // All series are Crypto for now
                endDate,
            });
        }
    }

    return Array.from(eventsMap.values());
}

/**
 * Load cached events list from localStorage.
 * Returns null if cache is missing or expired (24h TTL).
 */
export function loadEventsFromCache(): PolymarketEvent[] | null {
    try {
        const raw = localStorage.getItem(EVENTS_CACHE_KEY);
        if (!raw) return null;

        const cached = JSON.parse(raw);
        if (!cached.timestamp || !cached.events) return null;

        const age = Date.now() - cached.timestamp;
        if (age > EVENTS_CACHE_TTL_MS) {
            localStorage.removeItem(EVENTS_CACHE_KEY);
            return null;
        }

        return cached.events as PolymarketEvent[];
    } catch {
        return null;
    }
}

/**
 * Save events list to localStorage for stale-while-revalidate caching.
 */
export function saveEventsToCache(events: PolymarketEvent[]): void {
    try {
        localStorage.setItem(EVENTS_CACHE_KEY, JSON.stringify({
            timestamp: Date.now(),
            events,
        }));
    } catch {
        // localStorage full or unavailable - ignore silently
    }
}

/**
 * Transform metadata documents to MarketMetadata
 */
function transformToMarketMetadata(metadata: RawMetadataDoc[]): MarketMetadata[] {
    return metadata.map(m => {
        const { eventTitle, question, conditionId } = getMetadataFields(m);
        // Use m.series (the field) for fetching candles, m.id for unique identification
        const seriesValue = m.series || m.id;
        return {
            id: m.id, // doc ID for unique identification
            name: question || m.id, // Display name from question field
            eventId: eventTitle, // Event grouping
            clobTokenId: conditionId,
            series: seriesValue, // Use series field for fetching candle data
        };
    });
}

/**
 * Transform Firebase candle document to CandleDataPoint
 */
function transformFirebaseCandle(doc: RawCandleDoc): CandleDataPoint {
    const data = doc.data || {};

    // Note: market_info is no longer in candle data - it's now in _metadata
    return {
        timestamp: toDate(doc.timestamp),
        open: data.open || 0,
        high: data.high || 0,
        low: data.low || 0,
        close: data.close || 0,
        baseVolume: data.base_volume || 0,
        quoteVolume: data.quote_volume || 0,
        tradeCount: data.trade_count || 0,
        vwap: data.vwap || 0,
        openTimestamp: toDate(data.open_timestamp),
        closeTimestamp: toDate(data.close_timestamp),
        marketInfo: undefined,
        isFilled: false,
    };
}

/**
 * Transform CandleDataPoint to PriceTimeseries (for simple mode)
 */
function candleToPriceTimeseries(candle: CandleDataPoint): PriceTimeseries {
    return {
        timestamp: candle.timestamp.toISOString(),
        data: {
            price: {
                yes: candle.close,
                no: 1 - candle.close,
            },
        },
    };
}

/**
 * Create a getList function that uses the authenticated dataProvider
 */
export function createGetList(dataProvider: DataProvider) {
    return async function getList(
        resource: string,
        params: GetListParams
    ): Promise<GetListResult<any>> {
        const { pagination, sort, filter } = params;

        // Handle events metadata request
        if (resource === 'odds/_events') {
            const metadata = await fetchAllMetadata();
            let events = extractEventsFromMetadata(metadata);

            // Apply filtering if provided (e.g., by category)
            if (filter) {
                Object.entries(filter).forEach(([key, value]) => {
                    events = events.filter((item: any) => item[key] === value);
                });
            }

            // Apply sorting
            events.sort((a: any, b: any) => {
                const aVal = a[sort.field];
                const bVal = b[sort.field];

                if (sort.order === 'ASC') {
                    return aVal > bVal ? 1 : -1;
                } else {
                    return aVal < bVal ? 1 : -1;
                }
            });

            // Apply pagination
            const start = (pagination.page - 1) * pagination.perPage;
            const end = start + pagination.perPage;
            const paginatedData = events.slice(start, end);

            return {
                data: paginatedData,
                total: events.length,
            };
        }

        // Handle metadata request
        if (resource === 'odds/_metadata') {
            const metadata = await fetchAllMetadata();
            let markets = transformToMarketMetadata(metadata);

            // Apply filtering if provided (e.g., by eventId)
            if (filter) {
                if (filter.eventId) {
                    // Filter by event_title (which maps to eventId)
                    markets = markets.filter(m => m.eventId === filter.eventId);
                }
                // Apply other filters
                Object.entries(filter).forEach(([key, value]) => {
                    if (key !== 'eventId') {
                        markets = markets.filter((item: any) => item[key] === value);
                    }
                });
            }

            // Apply sorting
            markets.sort((a: any, b: any) => {
                const aVal = a[sort.field];
                const bVal = b[sort.field];

                if (sort.order === 'ASC') {
                    return aVal > bVal ? 1 : -1;
                } else {
                    return aVal < bVal ? 1 : -1;
                }
            });

            // Apply pagination
            const start = (pagination.page - 1) * pagination.perPage;
            const end = start + pagination.perPage;
            const paginatedData = markets.slice(start, end);

            return {
                data: paginatedData,
                total: markets.length,
            };
        }

        // Handle timeseries request for a specific market
        if (resource.startsWith('odds/')) {
            const seriesId = resource.replace('odds/', '');

            // Fetch candle data from Firebase using authenticated dataProvider
            // The series ID needs to be URL-encoded because Firebase collection names use encoded colons
            const encodedSeriesId = encodeURIComponent(seriesId);
            const candlePath = `timeseries/polymarket_candle/${encodedSeriesId}`;

            let candleDocs: any[] = [];
            try {
                const result = await dataProvider.getList(
                    candlePath,
                    {
                        pagination: { page: 1, perPage: pagination.perPage },
                        sort: { field: 'timestamp', order: sort.order },
                        filter: {},
                    }
                );
                candleDocs = result.data;
            } catch (error) {
                console.error('[getList] Error fetching candles for series:', seriesId, 'path:', candlePath, 'error:', error);
                throw error;
            }

            // Transform to CandleDataPoint
            const candles = (candleDocs as RawCandleDoc[]).map(transformFirebaseCandle);

            // Convert to PriceTimeseries (for simple mode compatibility)
            let timeseriesData = candles.map(candleToPriceTimeseries);

            // Apply filtering (e.g., timestamp range)
            if (filter) {
                if (filter.timestamp_gte) {
                    const minTime = new Date(filter.timestamp_gte).getTime();
                    timeseriesData = timeseriesData.filter(
                        item => new Date(item.timestamp).getTime() >= minTime
                    );
                }
                if (filter.timestamp_lte) {
                    const maxTime = new Date(filter.timestamp_lte).getTime();
                    timeseriesData = timeseriesData.filter(
                        item => new Date(item.timestamp).getTime() <= maxTime
                    );
                }
            }

            // Ensure sorted
            timeseriesData.sort((a, b) => {
                const aTime = new Date(a.timestamp).getTime();
                const bTime = new Date(b.timestamp).getTime();

                if (sort.order === 'ASC') {
                    return aTime - bTime;
                } else {
                    return bTime - aTime;
                }
            });

            return {
                data: timeseriesData,
                total: timeseriesData.length,
            };
        }

        // Unknown resource
        throw new Error(`Unknown resource: ${resource}`);
    };
}

/**
 * Get raw candle data (for full_series mode)
 * Returns CandleDataPoint[] instead of PriceTimeseries[]
 */
export async function getCandleData(
    dataProvider: DataProvider,
    seriesId: string,
    params: GetListParams
): Promise<CandleDataPoint[]> {
    // The series ID needs to be URL-encoded because Firebase collection names use encoded colons
    const encodedSeriesId = encodeURIComponent(seriesId);
    const candlePath = `timeseries/polymarket_candle/${encodedSeriesId}`;

    let candleDocs: any[] = [];
    try {
        const result = await dataProvider.getList(
            candlePath,
            {
                pagination: { page: 1, perPage: params.pagination.perPage },
                sort: { field: 'timestamp', order: params.sort.order },
                filter: {},
            }
        );
        candleDocs = result.data;
    } catch (error) {
        console.error('[getCandleData] Error fetching candles for series:', seriesId, 'path:', candlePath, 'error:', error);
        throw error;
    }

    let candles = (candleDocs as RawCandleDoc[]).map(transformFirebaseCandle);

    // Apply filtering (e.g., timestamp range)
    const { filter, sort } = params;
    if (filter) {
        if (filter.timestamp_gte) {
            const minTime = new Date(filter.timestamp_gte).getTime();
            candles = candles.filter(c => c.timestamp.getTime() >= minTime);
        }
        if (filter.timestamp_lte) {
            const maxTime = new Date(filter.timestamp_lte).getTime();
            candles = candles.filter(c => c.timestamp.getTime() <= maxTime);
        }
    }

    // Ensure sorted
    candles.sort((a, b) => {
        const diff = a.timestamp.getTime() - b.timestamp.getTime();
        return sort.order === 'ASC' ? diff : -diff;
    });

    return candles;
}

/**
 * Convert Firebase candle data to MarketAnalysisData format.
 * This enables using the Full Series chart with Firebase data.
 * bidAsk, fairValue, and trades arrays will be empty since
 * Firebase only provides candle data.
 */
export function firebaseCandlesToMarketAnalysisData(
    candles: CandleDataPoint[]
): MarketAnalysisData {
    return {
        candles,
        bidAsk: [],      // Not available from Firebase
        fairValue: [],   // Not available from Firebase
        trades: [],      // Not available from Firebase
    };
}

/**
 * Clear metadata cache (useful for forcing refresh or freeing memory)
 * Call this when switching categories/events to reduce memory usage.
 */
export function clearMetadataCache(): void {
    if (cacheExpirationTimer) {
        clearTimeout(cacheExpirationTimer);
        cacheExpirationTimer = null;
    }
    metadataCache = { data: null, fetchedAt: 0, documentCount: 0 };
}

/**
 * Get metadata cache statistics for debugging
 */
export function getMetadataCacheStats(): {
    documentCount: number;
    ageMs: number;
    isExpired: boolean;
} {
    const now = Date.now();
    const ageMs = metadataCache.fetchedAt ? now - metadataCache.fetchedAt : 0;
    return {
        documentCount: metadataCache.documentCount,
        ageMs,
        isExpired: ageMs > CACHE_TTL,
    };
}

// ============================================================
// Polymarket Event/Market Extraction Functions
// (Group by event_title for manageable dropdown sizes)
// ============================================================

/**
 * Capitalize the first letter of a string.
 */
function capitalize(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Extract unique Polymarket events from metadata documents.
 * Groups by market_info.event_title to collapse multiple tokens/outcomes
 * into their parent event.
 * Filters out series where first_timestamp === last_timestamp (single data point).
 *
 * @param metadata - Array of raw metadata documents
 * @returns Array of PolymarketEvent for dropdown population
 */
export function extractPolymarketEvents(
    metadata: RawMetadataDoc[]
): PolymarketEvent[] {
    const eventMap = new Map<string, PolymarketEvent>();

    for (const doc of metadata) {
        const eventTitle = doc.market_info?.event_title;
        if (!eventTitle) continue;

        // Filter: skip single data point series
        if (doc.first_timestamp !== undefined &&
            doc.last_timestamp !== undefined &&
            doc.first_timestamp === doc.last_timestamp) {
            continue;
        }

        if (!eventMap.has(eventTitle)) {
            eventMap.set(eventTitle, {
                id: eventTitle,
                displayName: eventTitle,
                eventTitle: eventTitle,
            });
        }
    }

    const events = Array.from(eventMap.values());

    // Persist to localStorage for instant loading on next visit
    saveEventsToCache(events);

    return events;
}

/**
 * Extract markets for a specific Polymarket event (by event_title).
 * Each market represents one outcome token (yes/no/team) of the event.
 * For each unique token, keeps the doc with the smallest period (finest granularity)
 * to maximize frequency options.
 *
 * @param metadata - Array of raw metadata documents
 * @param eventTitle - Event title to filter by (from PolymarketEvent.id)
 * @returns Array of PolymarketMarket for dropdown population
 */
export function extractPolymarketMarkets(
    metadata: RawMetadataDoc[],
    eventTitle: string
): PolymarketMarket[] {
    const tokenMap = new Map<string, RawMetadataDoc>();

    for (const doc of metadata) {
        if (doc.market_info?.event_title !== eventTitle) continue;
        const tokenId = doc.name;
        if (!tokenId) continue;

        // Filter: skip single data point series
        if (doc.first_timestamp !== undefined &&
            doc.last_timestamp !== undefined &&
            doc.first_timestamp === doc.last_timestamp) {
            continue;
        }

        // Keep finest-grained period doc per token
        const existing = tokenMap.get(tokenId);
        if (!existing || (doc.period && existing.period && doc.period < existing.period)) {
            tokenMap.set(tokenId, doc);
        }
    }

    return Array.from(tokenMap.values()).map(doc => ({
        id: doc.id,
        entity: capitalize(doc.entity || 'Unknown'),
        eventId: doc.name || '',
        series: doc.series || doc.id,
        firstTimestamp: doc.first_timestamp || 0,
        lastTimestamp: doc.last_timestamp || 0,
        periodMs: (doc.period || 60_000_000_000) / 1_000_000,
    }));
}

/**
 * Convert raw metadata documents to PolymarketCandleMetadata type.
 * Provides type-safe access to all metadata fields.
 */
export function toPolymarketCandleMetadata(
    docs: RawMetadataDoc[]
): PolymarketCandleMetadata[] {
    return docs.map(doc => ({
        id: doc.id,
        entity: doc.entity || '',
        first_timestamp: doc.first_timestamp || 0,
        last_timestamp: doc.last_timestamp || 0,
        name: doc.name || '',
        period: doc.period || 0,
        series: doc.series || doc.id,
        source: doc.source || '',
        type: doc.type || '',
        market_info: doc.market_info,
    }));
}

/**
 * Fetch all metadata documents and return them typed as PolymarketCandleMetadata.
 * Uses direct Firestore access and the existing in-memory cache.
 */
export async function fetchPolymarketMetadata(): Promise<PolymarketCandleMetadata[]> {
    const rawDocs = await fetchAllMetadata();
    return toPolymarketCandleMetadata(rawDocs);
}

// Legacy export for backwards compatibility - will throw error if used without dataProvider
export async function getList(
    resource: string,
    params: GetListParams
): Promise<GetListResult<any>> {
    throw new Error(
        'getList requires dataProvider. Use createGetList(dataProvider) instead, ' +
        'or import useOddsDataProvider hook from this module.'
    );
}
